// SideMenu.js
import { SidemenuData } from "./SidemenuData";
import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "./FirebaseConfig.js";
import { doc, onSnapshot } from "firebase/firestore";
import { Tooltip, Button, Modal, message } from "antd";
import { SmileOutlined, LogoutOutlined, BlockOutlined, ReadOutlined, KeyOutlined } from "@ant-design/icons";
import { ReactComponent as Icon } from "./LogWhite.svg";
import { useTranslation } from "react-i18next";

const SideMenu = () => {
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [userDoc, setUserDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("i18nextLng", lng);
  };

  // 認証状態 + Firestore user ドキュメント取得
  useEffect(() => {
    let unsubUserDoc = null;

    const unsubAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      if (!currentUser) {
        setUserDoc(null);
        setLoading(false);
        if (unsubUserDoc) unsubUserDoc();
        return;
      }

      const ref = doc(db, "users", currentUser.uid);

      // 🔥 Firestore をリアルタイム購読
      unsubUserDoc = onSnapshot(
        ref,
        (snap) => {
          setUserDoc(snap.exists() ? snap.data() : null);
          setLoading(false);
        },
        (err) => {
          console.error(err);
          message.error(t("sidemenu.fetchUserFailed"));
          setLoading(false);
        }
      );
    });

    return () => {
      unsubAuth();
      if (unsubUserDoc) unsubUserDoc();
    };
  }, []);


  /* =======================
     利用規約 同意チェック
     ======================= */
  const agreed =
    userDoc?.agreeTerms === true && userDoc?.agreePrivacy === true;

  /* =======================
     契約プランのチェック
     ======================= */
  const sub = userDoc?.subscription || null;
  const hasContract = !!(sub && sub.endAt && sub.endAt.toDate);
  const leftDays = (() => {
    if (!hasContract) return null;
    const end = sub.endAt.toDate();
    const endY = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const now = new Date();
    const todayY = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msDay = 24 * 60 * 60 * 1000;
    return Math.ceil((endY - todayY) / msDay);
  })();

  const isExpiredOrMissing = leftDays !== null && leftDays < 0;

  /* =======================
      メニュークリック処理
     ======================= */
  const handleItemClick = (item) => {
    const isPublic = item.public === true;

    // 🔥 規約未同意 → 全ての非 public メニューをブロック
    if (!agreed && !isPublic) {
      message.warning(t("sidemenu.needAgreement"));
      navigate("/terms");
      return;
    }


    // 🔥 契約切れ/未登録 → 非 public メニューをブロック
    if (isExpiredOrMissing && !isPublic) {
      message.warning(t("sidemenu.contractWarning"));
      return;
    }

    navigate(item.link);
  };


  const showModal = () => setOpen(true);
  const hideModal = () => setOpen(false);

  const logout = async () => {
    await signOut(auth);
    navigate("/login/");
    message.success(t("sidemenu.loggedOut"));
    setOpen(false);
  };

  if (loading) {
    return (
      <div className="SidebarList" style={{ padding: 16 }}>
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div>
      {!user ? (
        <div className="SidebarList">
          <a href={t("common.hqhomeurl")} target="_blank" rel="noreferrer">
            <div className="AvaterIcon">
              <Icon width={100} height={100}  />
            </div>
          </a>
          <div className="AvaterIcon">
            <Link to={`/Login/`}>
              <Button>{t("sidemenu.login")}</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div>
          <ul className="SidebarList">
            <a href={t("common.hqhomeurl")} target="_blank" rel="noreferrer">
              <div className="AvaterIcon">
                <Icon width={100} height={100} />
              </div>
            </a>

            <div className="AvaterIcon">
              <Tooltip
                placement="right"
                title={`${user.email}`}
                color={!agreed ? "#ff3b30" : isExpiredOrMissing ? "#ff1d21" : "#61a0ff"}
              >
                <SmileOutlined
                  style={{
                    fontSize: "30px",
                    color: !agreed
                      ? "#ff3b30" // 規約未同意 → 赤
                      : isExpiredOrMissing
                        ? "#ff1d21" // 契約切れ → 赤
                        : "#61a0ff", // 有効 → 青
                  }}
                />
              </Tooltip>
            </div>

            {SidemenuData.map((value, key) => {
              const isActive = window.location.pathname === value.link;
              const isPublic = value.public === true;
              const isTermsPage = value.link === "/terms";
              const disabled =
                (!agreed || isExpiredOrMissing) && !isPublic && !isTermsPage;

              const Row = (
                <li
                  key={key}
                  id={isActive ? "active" : ""}
                  className={`row ${disabled ? "row-disabled" : ""}`}
                  onClick={() => handleItemClick(value)}
                  aria-disabled={disabled}
                  style={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.45 : 1,
                  }}
                >
                  <div id="icon">
                    {disabled ? <KeyOutlined /> : value.icon}
                  </div>
                  <div id="title">{t(value.titleKey)}</div>
                </li>
              );

              return disabled ? (
                <Tooltip
                  key={key}
                  placement="right"
                  title={
                    isTermsPage
                      ? agreed
                        ? t("sidemenu.alreadyAgreed")
                        : t("sidemenu.needAgreement")
                      : !agreed
                        ? t("sidemenu.needAgreement")
                        : t("sidemenu.contractTooltip")
                  }
                >
                  {Row}
                </Tooltip>
              ) : (
                Row
              );
            })}


            <div style={{ height: 24 }} />

            <div className="AvaterIcon">
              <Button
                danger
                className="AvaterIcon"
                onClick={showModal}
                icon={<LogoutOutlined />}
              >
                {t("sidemenu.logout")}
              </Button>
            </div>
          </ul>
        </div>
      )}

      {/* サポートページ */}
      <div style={{ textAlign: "center", marginTop: 16 }}>
        <a href={t("mypage.suportPageurl")} target="_blank" rel="noreferrer">
          <Button style={{ marginTop: 8 }}>{t("mypage.supportSite")}</Button>
        </a>
      </div>

      {/* 言語切替 */}
      <div style={{ textAlign: "center", marginTop: 40 }}>
        <Button
          size="small"
          type={currentLang === "en" ? "primary" : "default"}
          onClick={() => changeLanguage("en")}
          style={{ marginRight: 5 }}
        >
          English
        </Button>
        <Button
          size="small"
          type={currentLang === "ja" ? "primary" : "default"}
          onClick={() => changeLanguage("ja")}
        >
          日本語
        </Button>
      </div>

      {/* ログアウト確認 */}
      <Modal
        title={t("sidemenu.logoutTitle")}
        open={open}
        onOk={logout}
        onCancel={hideModal}
        okText={t("common.yes")}
        cancelText={t("common.no")}
      >
        <div>{t("sidemenu.logoutConfirm")}</div>
      </Modal>
    </div>
  );
};

export default SideMenu;
